import type { BuildResult, MapartPalette, MapartSettings, SectionMap } from './types';

/** Main-thread handle to the off-thread mapart build. The worker owns the palette after init. */
export class MapartWorkerClient {
  private readonly worker: Worker;
  private readonly pending = new Map<number, (result: BuildResult) => void>();
  private nextId = 1;

  constructor(palette: MapartPalette) {
    this.worker = new Worker(new URL('./mapartWorker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (event: MessageEvent<{ id: number; result: BuildResult }>) => {
      const resolve = this.pending.get(event.data.id);
      if (!resolve) return;
      this.pending.delete(event.data.id);
      resolve(event.data.result);
    };
    this.worker.postMessage({ kind: 'init', palette });
  }

  build(
    rgba: Uint8ClampedArray,
    width: number,
    length: number,
    settings: MapartSettings,
    sectionMap: SectionMap | null,
  ): Promise<BuildResult> {
    const id = this.nextId++;
    const copy = new Uint8ClampedArray(rgba);
    const sections = sectionMap
      ? { owners: new Uint8Array(sectionMap.owners), sections: sectionMap.sections }
      : null;
    return new Promise<BuildResult>((resolve) => {
      this.pending.set(id, resolve);
      this.worker.postMessage(
        { kind: 'build', id, rgba: copy, width, length, settings, sections },
        sections ? [copy.buffer, sections.owners.buffer] : [copy.buffer],
      );
    });
  }

  dispose() {
    this.pending.clear();
    this.worker.terminate();
  }
}
